import { apiClient } from '../api/client'
import type { Event, Task, TaskStatus, Supplier, Message, Suggestion } from '../types/mockup'
// TODO: do we need event service now that we use react query
export interface CommandRequest {
  message: string
  eventId: string
}

export interface CommandResponse {
  success: boolean
  message?: string
  suggestion?: Suggestion
  updatedTasks?: Task[]
}

export const eventService = {
  // Event operations
  getEvent: async (eventId: string): Promise<Event> => {
    const response = await apiClient.get(`/events/${eventId}`)
    const data = response.data

    // Transform date strings to Date objects for tasks
    if (data.tasks) {
      data.tasks = data.tasks.map((task: any) => ({
        ...task,
        startTime: new Date(task.startTime),
        endTime: new Date(task.endTime),
        createdAt: task.createdAt ? new Date(task.createdAt) : undefined,
        updatedAt: task.updatedAt ? new Date(task.updatedAt) : undefined,
      }))
    }

    // Transform date strings for suppliers
    if (data.suppliers) {
      data.suppliers = data.suppliers.map((supplier: any) => ({
        ...supplier,
        lastContact: supplier.lastContact ? new Date(supplier.lastContact) : undefined,
      }))
    }

    // Transform event dates
    if (data.date) {
      data.date = new Date(data.date)
    }
    if (data.startTime) {
      data.startTime = new Date(data.startTime)
    }
    if (data.endTime) {
      data.endTime = new Date(data.endTime)
    }
    if (data.createdAt) {
      data.createdAt = new Date(data.createdAt)
    }
    if (data.updatedAt) {
      data.updatedAt = new Date(data.updatedAt)
    }

    return data
  },

  getEventTimeline: async (eventId: string) => {
    const response = await apiClient.get(`/events/${eventId}/timeline`)
    return response.data
  },

  // Task operations
  getTasks: async (eventId: string, status?: TaskStatus): Promise<Task[]> => {
    const params = status ? { status } : {}
    const response = await apiClient.get(`/events/${eventId}/tasks`, { params })

    // Transform date strings to Date objects
    return response.data.map((task: any) => ({
      ...task,
      startTime: new Date(task.startTime),
      endTime: new Date(task.endTime),
      createdAt: task.createdAt ? new Date(task.createdAt) : undefined,
      updatedAt: task.updatedAt ? new Date(task.updatedAt) : undefined,
    }))
  },

  updateTaskStatus: async (eventId: string, taskId: string, status: TaskStatus): Promise<Task> => {
    const response = await apiClient.patch(`/tasks/${taskId}/status?event_id=${eventId}`, {
      status,
    })

    // Transform date strings to Date objects
    const task = response.data
    return {
      ...task,
      startTime: new Date(task.startTime),
      endTime: new Date(task.endTime),
      createdAt: task.createdAt ? new Date(task.createdAt) : undefined,
      updatedAt: task.updatedAt ? new Date(task.updatedAt) : undefined,
    }
  },

  // Supplier operations
  getSuppliers: async (eventId: string): Promise<Supplier[]> => {
    const response = await apiClient.get(`/events/${eventId}/suppliers`)
    return response.data
  },

  getSupplier: async (eventId: string, supplierId: string): Promise<Supplier> => {
    const response = await apiClient.get(`/events/${eventId}/suppliers/${supplierId}`)
    return response.data
  },

  // Command processing
  processCommand: async (eventId: string, command: string): Promise<CommandResponse> => {
    const response = await apiClient.post(`/events/${eventId}/command`, {
      message: command,
      eventId,
    })
    return response.data
  },

  // Current status
  getCurrentStatus: async (eventId: string) => {
    const response = await apiClient.get(`/events/${eventId}/current-status`)
    return response.data
  },

  // Messages
  getMessages: async (eventId: string): Promise<Message[]> => {
    const response = await apiClient.get(`/events/${eventId}/messages`)
    return response.data
  },

  // Suggestions
  getSuggestions: async (
    eventId: string,
    params?: {
      status?: string
      risk_level?: string
      confidence_min?: number
      confidence_max?: number
    }
  ) => {
    const response = await apiClient.get(`/events/${eventId}/suggestions`, { params })
    return response.data
  },

  getSuggestion: async (eventId: string, suggestionId: string) => {
    const response = await apiClient.get(`/events/${eventId}/suggestions/${suggestionId}`)
    return response.data
  },

  approveSuggestion: async (eventId: string, suggestionId: string) => {
    const response = await apiClient.post(`/events/${eventId}/suggestions/${suggestionId}/approve`)
    return response.data
  },

  rejectSuggestion: async (eventId: string, suggestionId: string) => {
    const response = await apiClient.post(`/events/${eventId}/suggestions/${suggestionId}/reject`)
    return response.data
  },
}

export default eventService
